/**
 * Canonical "pick a representative printing" logic.
 *
 * Single source of truth for choosing which printing of a card should represent
 * it in the UI (e.g. the blog `[[card]]` hover overlay). The goal is
 * *recognizability*: prefer the original, English, standard-frame printing over
 * newer promos / special-frame variants.
 *
 * Priority (a total ordering — see `displayPrintingComparator`):
 *   1. English first (`lang === 'en'`).
 *   2. PRIMARY KEY: earliest `releasedAt` (oldest printing first).
 *   3. Tiebreaker: prefer non-digital printings (skip Arena/MTGO-only).
 *   4. Tiebreaker: prefer black-border printings (skip borderless/promo frames).
 *   5. Deterministic final tiebreak: `setCode`, then `collectorNumber`.
 *
 * IMPORTANT — the frame/digital preferences are *tiebreakers, never filters*.
 * They only reorder printings that are otherwise equal on the higher-priority
 * keys; they never exclude a printing. A card whose only printings are digital,
 * or whose only printings are non-black-border, still resolves to one of them.
 *
 * HEURISTIC NOTE: the MTGCard model has no true `frame` / `promoType` /
 * `frameEffects` field, so "standard frame" is *approximated* from `borderColor`
 * and `digital`. This is a heuristic, not a precise frame check. If a real frame
 * field is added to the card model later, refine steps 3-4 to use it.
 */

export interface DisplayPrintingInput {
  lang?: string | null;
  releasedAt?: string | Date | null;
  borderColor?: string | null;
  digital?: boolean | null;
  setCode?: string | null;
  collectorNumber?: string | null;
}

/** Undated printings sort last so any dated printing is preferred. */
const releaseTime = (p: DisplayPrintingInput): number => {
  if (!p.releasedAt) return Number.POSITIVE_INFINITY;
  const t = new Date(p.releasedAt).getTime();
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
};

const isEnglish = (p: DisplayPrintingInput): boolean =>
  (p.lang ?? '').toLowerCase() === 'en';

/**
 * Comparator implementing the priority above. Stable, total ordering — safe to
 * pass straight to `Array.prototype.sort`. Returns negative when `a` should rank
 * before `b`.
 */
export function displayPrintingComparator(
  a: DisplayPrintingInput,
  b: DisplayPrintingInput
): number {
  // 1. English first.
  const enDiff = Number(isEnglish(b)) - Number(isEnglish(a));
  if (enDiff !== 0) return enDiff;

  // 2. Earliest release date (primary key).
  const dateDiff = releaseTime(a) - releaseTime(b);
  if (dateDiff !== 0) return dateDiff;

  // 3. Tiebreaker: non-digital before digital (undefined treated as physical).
  const digitalDiff = Number(a.digital === true) - Number(b.digital === true);
  if (digitalDiff !== 0) return digitalDiff;

  // 4. Tiebreaker: black border before anything else.
  const blackDiff =
    Number(b.borderColor === 'black') - Number(a.borderColor === 'black');
  if (blackDiff !== 0) return blackDiff;

  // 5. Deterministic final tiebreak so the same card always resolves identically.
  const setDiff = (a.setCode ?? '').localeCompare(b.setCode ?? '');
  if (setDiff !== 0) return setDiff;

  return (a.collectorNumber ?? '').localeCompare(b.collectorNumber ?? '', undefined, {
    numeric: true,
  });
}

/**
 * Returns the representative ("original / most recognizable") printing from a
 * list, or `undefined` if the list is empty. Does not mutate the input.
 */
export function selectDisplayPrinting<T extends DisplayPrintingInput>(
  printings: T[] | null | undefined
): T | undefined {
  if (!printings || printings.length === 0) return undefined;
  return [...printings].sort(displayPrintingComparator)[0];
}
